import type {Observable, OperatorFunction, Subscription} from 'rxjs';
import {exists} from './custom-utils';
import {bufferWhen, debounceTime, share} from 'rxjs/operators';
import {ReplaySubject, Subject} from 'rxjs';

export function shareWithRefCountAndReplayLast() {
    return <T>(source: Observable<T>): Observable<T> => {
        return source.pipe(
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true
            })
        )
    }
}

export function shareWithRefCount() {
    return <T>(source: Observable<T>): Observable<T> => {
        return source.pipe(
            share({
                connector: () => new Subject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true
            })
        )
    }
}

export function unsubscribeAllSubscriptions(...subscriptions: (Subscription | undefined)[]) {
    subscriptions.forEach((sub) => {
        if (exists(sub)) {
            sub.unsubscribe();
        }
    })
}

/**
 * AI generated:
 * Custom RxJS operator that buffers emissions from the source Observable.
 * Buffering starts implicitly with the first emission relevant to a buffer window.
 * The buffer is emitted only after a period of silence (specified by debounceMs)
 * has occurred on the source stream following the last emission.
 *
 * @template T The type of items emitted by the source Observable.
 * @param {number} debounceMs The time in milliseconds of inactivity required on the source
 * stream to close and emit the current buffer.
 * @returns {OperatorFunction<T, T[]>} An RxJS operator function that transforms an
 * Observable<T> emitting single items into an Observable<T[]> emitting arrays (buffers)
 * of those items.
 */
export function bufferDebounce<T>(debounceMs: number): OperatorFunction<T, T[]> {
    // Return the actual operator logic as a function that takes the source
    return (source: Observable<T>): Observable<T[]> => {
        // The core logic remains the same: use bufferWhen with a debounced source as the closer.
        return source.pipe(
            bufferWhen(() => {
                    // The closing notifier: debounce the *source* stream itself.
                    // When the source stops emitting for `debounceMs`, this inner observable emits,
                    // triggering the buffer to close and emit its contents.
                    return source.pipe(debounceTime(debounceMs));
                }
            )
            // Optional: You might want to filter out empty buffers if they could occur
            // in specific edge cases (e.g., source completing very quickly after subscribing
            // before debounce timer starts, though debounceTime usually handles this well).
            // filter(buffer => buffer.length > 0)
        );
    };
}